import React from "react";
import Switch from "@mui/material/Switch";
import Tooltip from "@mui/material/Tooltip";

export type SiteStatusKey = "siteDisabled" | "siteMaintenance";

type Props = {
    siteDisabled: boolean,
    siteMaintenance: boolean,
    invalidJson: boolean,
    disabled: boolean,
    onToggle: (key: SiteStatusKey, value: boolean) => void
}

const OPTIONS: { key: SiteStatusKey, label: string, description: string, color: "error" | "warning" }[] = [
    {
        key: "siteDisabled",
        label: "Site disabled",
        description: "The storefront shows an \"unavailable\" page instead of the site.",
        color: "error",
    },
    {
        key: "siteMaintenance",
        label: "Maintenance mode",
        description: "The storefront shows the maintenance page.",
        color: "warning",
    },
];

const getState = (siteDisabled: boolean, siteMaintenance: boolean) => {
    if (siteMaintenance) return { key: "maintenance", label: "Maintenance" };
    if (siteDisabled) return { key: "disabled", label: "Disabled" };
    return { key: "live", label: "Live" };
};

export default function StoreSiteStatus({ siteDisabled, siteMaintenance, invalidJson, disabled, onToggle }: Props) {
    const values: Record<SiteStatusKey, boolean> = { siteDisabled, siteMaintenance };
    const state = getState(siteDisabled, siteMaintenance);

    return (
        <div className={`site-status site-status--${state.key}`}>
            <div className="site-status__bar">
                <div className="site-status__summary">
                    <p className="site-status__title">Site status</p>
                    <span className={`site-status__badge site-status__badge--${state.key}`}>
                        <span className="site-status__dot" />
                        {state.label}
                    </span>
                </div>
                <div className="site-status__options">
                    {OPTIONS.map(({ key, label, description, color }) => (
                        <Tooltip title={invalidJson ? "Fix the JSON below to use this option." : description} key={key}>
                            <label className={`site-status__option ${values[key] ? `site-status__option--on-${color}` : ""}`}>
                                <span className="site-status__label">{label}</span>
                                <Switch
                                    size="small"
                                    color={color}
                                    checked={values[key]}
                                    disabled={disabled || invalidJson}
                                    onChange={() => onToggle(key, !values[key])}
                                />
                            </label>
                        </Tooltip>
                    ))}
                </div>
            </div>
        </div>
    );
}
